import React from 'react';

function visibleRtgWork(items, startMs, spanMs) {
  const endMs = startMs + spanMs;
  const seen = new Set();
  return items.filter(item => item.plannedFinishMs > item.plannedStartMs
    && item.plannedStartMs < endMs && item.plannedFinishMs > startMs)
    .map((item, index) => ({ ...item, id: `${item.reason.type}-${item.reason.id || item.reason.jcn || index}`,
      start: Math.max(startMs, item.plannedStartMs), end: Math.min(endMs, item.plannedFinishMs) }))
    .filter(item => {
      const key = `${item.id}|${item.start}|${item.end}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.start - b.start || a.end - b.end || a.id.localeCompare(b.id));
}

function laneRtgWork(visible) {
  const maintenance = visible.filter(item => item.driverCategory?.key !== 'alert');
  const alerts = visible.filter(item => item.driverCategory?.key === 'alert');
  const laneEnds = [];
  const segments = maintenance.map(item => {
    let lane = laneEnds.findIndex(end => end <= item.start);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = item.end;
    return { ...item, lane };
  });
  // Alert has its own lane, below all maintenance. Overlapping Alert plans
  // share that lane because they represent the same unavailable status.
  segments.push(...alerts.map(item => ({ ...item, lane: laneEnds.length })));
  return { segments, lanes: laneEnds.length + (alerts.length ? 1 : 0) };
}

export function stackedDriverStripeBackground(driverCategories, highlightedDriverKeys = [], highlightsActive = false) {
  const uniqueCategories = [];
  const seen = new Set();
  driverCategories.forEach(category => {
    if (!category?.color) return;
    const signature = `${category.key || 'other'}|${category.color}`;
    if (seen.has(signature)) return;
    seen.add(signature);
    uniqueCategories.push(category);
  });
  const selected = new Set(highlightedDriverKeys);
  const colors = uniqueCategories.map((category, index) => (
    highlightsActive && !selected.has(category.key)
      ? (index % 2 ? 'rgba(98, 118, 130, 0.34)' : 'rgba(98, 118, 130, 0.22)')
      : category.color
  ));
  if (!colors.length) return undefined;
  if (colors.length === 1) return `linear-gradient(${colors[0]}, ${colors[0]})`;
  const stripeWidth = 6;
  const stops = colors.flatMap((color, index) => [
    `${color} ${index * stripeWidth}px`,
    `${color} ${(index + 1) * stripeWidth}px`,
  ]);
  return `repeating-linear-gradient(135deg, ${stops.join(', ')})`;
}

export function layoutUnstackedRtgWork(items, startMs, spanMs) {
  const { segments, lanes } = laneRtgWork(visibleRtgWork(items, startMs, spanMs));
  return {
    height: Math.max(32, 16 + lanes * 16),
    segments: segments.map(item => ({
      id: item.id, label: item.label, shortLabel: item.shortLabel, estimated: item.estimated,
      driverKey: item.driverCategory?.key || 'other',
      driverClass: item.driverCategory?.className || 'is-driver-other',
      lane: item.lane, top: 8 + item.lane * 16,
      left: `${(item.start - startMs) / spanMs * 100}%`,
      width: `${(item.end - item.start) / spanMs * 100}%`,
    })),
  };
}

export function layoutStackedRtgWork(items, startMs, spanMs) {
  const visible = visibleRtgWork(items, startMs, spanMs);
  const boundaries = [...new Set(visible.flatMap(item => [item.start, item.end]))].sort((a, b) => a - b);
  const intervals = [];
  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const start = boundaries[index];
    const end = boundaries[index + 1];
    const active = visible.filter(item => item.start < end && item.end > start);
    if (!active.length) continue;
    const signature = active.map(item => item.id).sort().join('|');
    const previous = intervals[intervals.length - 1];
    if (previous?.signature === signature && previous.end === start) {
      previous.end = end;
    } else {
      intervals.push({ start, end, active, signature });
    }
  }
  return {
    height: 18,
    segments: intervals.map((interval, index) => {
      const overlap = interval.active.length > 1;
      const item = interval.active[0];
      const driverCategories = interval.active.map(activeItem => activeItem.driverCategory || {
        key: 'other', color: '#627682', className: 'is-driver-other',
      });
      return {
        id: `stacked-${index}-${interval.signature}`,
        label: overlap
          ? [`${interval.active.length} overlapping readiness drivers`, ...interval.active.map(activeItem => activeItem.label)].join('\n')
          : item.label,
        shortLabel: overlap ? `${interval.active.length} drivers` : item.shortLabel,
        estimated: interval.active.some(activeItem => activeItem.estimated),
        driverClass: overlap ? 'is-driver-overlap' : item.driverCategory?.className || 'is-driver-other',
        driverKeys: [...new Set(driverCategories.map(category => category.key))],
        driverCategories,
        overlapBackground: stackedDriverStripeBackground(driverCategories),
        hasDivider: index > 0,
        top: 3,
        height: 12,
        showLabel: true,
        left: `${(interval.start - startMs) / spanMs * 100}%`,
        width: `${(interval.end - interval.start) / spanMs * 100}%`,
      };
    }),
  };
}

export default function UnstackedRtgWork({ windows, layout, highlightSet = new Set(), highlightsActive = false }) {
  return <>
    {windows.map(window => <b key={window.id} className={`is-work${highlightsActive ? ' is-rtg-muted' : ''}`} style={{ left: window.left, width: window.width }} title={window.label} />)}
    {layout.segments.map(segment => <span key={segment.id}
      className={`crystal-ball__rtg-work-segment ${segment.driverClass}${segment.estimated ? ' is-estimated' : ''}${highlightsActive ? (highlightSet.has(segment.driverKey) ? ' is-rtg-highlighted' : ' is-rtg-muted') : ''}`}
      style={{ left: segment.left, width: segment.width, top: segment.top }}
      title={segment.label}>
      <small>{segment.shortLabel}</small>
    </span>)}
  </>;
}
